import * as Sentry from '@sentry/react';
import { WebSocket } from 'partysocket';
import ReconnectingWebSocket from 'partysocket/ws';
import { useCallback, useEffect, useRef, useState } from 'react';

import { BARRY_WEBSOCKET_MESSAGE_TYPES } from '~components/business-owner/barry/constants';
import { WebsocketMessengerMessageType } from '~components/business-owner/messenger/constants';

import { CustomEventName, useCustomEvent } from '~hooks/useCustomEvent';

import { useBarryWebsocketConnectionUrl } from '~api/common/barry/queries/useBarryWebsocketConnectionUrl';
import { useWebSocket } from '~contexts/WebSocketContext';
import { WEBSOCKET_STATUS, WebSocketConnectionStatus } from '~contexts/WebSocketContext';
import { REACT_ENV, VITE_ENV_TYPES } from '~utils/config';

const PING_INTERVAL = 60000; // 1 minute
const URL_REFRESH_INTERVAL = 300000; // 5 minutes
const MAX_RETRIES = 3;

export const useBarryWebsocket = () => {
	const { getConnection, setConnection, getConnectionStatus } = useWebSocket();
	const [connectionStatus, setConnectionStatus] = useState<WebSocketConnectionStatus>(
		WEBSOCKET_STATUS.DISCONNECTED,
	);
	const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const urlRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const retryCountRef = useRef<number>(0);
	const isMountedRef = useRef(true);
	const isRefreshingRef = useRef(false);
	const currentUrlRef = useRef<string | null>(null);
	const { publish: publishBarryConversationUpdated } = useCustomEvent(
		CustomEventName.BARRY_CONVERSATION_UPDATED,
	);
	const { publish: publishReportsBuilderConversationUpdated } = useCustomEvent(
		CustomEventName.REPORTS_BUILDER_CONVERSATION_UPDATED,
	);

	const { data: connectionData, fetchFreshUrl } = useBarryWebsocketConnectionUrl();

	const clearAllIntervals = useCallback(() => {
		if (pingIntervalRef.current) {
			clearInterval(pingIntervalRef.current);
			pingIntervalRef.current = null;
		}

		if (urlRefreshIntervalRef.current) {
			clearInterval(urlRefreshIntervalRef.current);
			urlRefreshIntervalRef.current = null;
		}
	}, []);

	const teardownConnection = useCallback(
		(url: string) => {
			const existingConnection = getConnection(url);

			if (existingConnection) {
				console.log('[Barry] Tearing down connection for URL:', url.slice(-20));
				existingConnection.reconnect = () => {};
				existingConnection.close();
				setConnection(url, null);
			}
		},
		[getConnection, setConnection],
	);

	const processWebsocketEvent = useCallback(
		(event: MessageEvent<string>) => {
			try {
				const data = JSON.parse(event.data);

				publishBarryConversationUpdated(data);

				if (data.type === BARRY_WEBSOCKET_MESSAGE_TYPES.REPORT_STAGE) {
					publishReportsBuilderConversationUpdated(data);
				}
			} catch (error) {
				console.error('Error processing websocket message:', error);
				Sentry.captureException(error);
			}
		},
		[publishBarryConversationUpdated, publishReportsBuilderConversationUpdated],
	);

	const refreshConnectionUrl = useCallback(async () => {
		if (isRefreshingRef.current || !isMountedRef.current) {
			return;
		}

		isRefreshingRef.current = true;

		try {
			if (currentUrlRef.current) {
				teardownConnection(currentUrlRef.current);
			}

			const { data } = await fetchFreshUrl();

			if (data?.url && isMountedRef.current) {
				retryCountRef.current = 0;
			}
		} catch (error) {
			Sentry.captureException(error);
		} finally {
			isRefreshingRef.current = false;
		}
	}, [fetchFreshUrl, teardownConnection]);

	const setupWebSocket = useCallback(
		(url: string) => {
			if (!isMountedRef.current || !url) {
				return;
			}

			// don't reconnect to the same URL we already have open
			if (currentUrlRef.current === url) {
				const existingConnection = getConnection(url);
				if (existingConnection?.readyState === WebSocket.OPEN) {
					setConnectionStatus(WEBSOCKET_STATUS.CONNECTED);
					return;
				}
			}

			// clean up any existing connection
			if (currentUrlRef.current && currentUrlRef.current !== url) {
				teardownConnection(currentUrlRef.current);
			}

			console.log('[Barry] Creating new WebSocket connection to:', url);
			setConnectionStatus(WEBSOCKET_STATUS.CONNECTING);

			const wsConnection = new ReconnectingWebSocket(url, [], {
				WebSocket: window.WebSocket,
				connectionTimeout: 4000,
				minReconnectionDelay: 2000,
				maxReconnectionDelay: 10000,
				maxRetries: MAX_RETRIES,
				maxEnqueuedMessages: 10,
				debug: REACT_ENV === VITE_ENV_TYPES.LOCAL,
			});

			wsConnection.onopen = () => {
				setConnectionStatus(WEBSOCKET_STATUS.CONNECTED);
				retryCountRef.current = 0;

				if (pingIntervalRef.current) {
					clearInterval(pingIntervalRef.current);
				}

				const channelId = new URL(url).searchParams.get('channelId');
				const pingMessage = {
					channelId,
					data: JSON.stringify({ type: WebsocketMessengerMessageType.PING }),
				};

				pingIntervalRef.current = setInterval(() => {
					if (wsConnection.readyState === WebSocket.OPEN) {
						wsConnection.send(JSON.stringify(pingMessage));
					}
				}, PING_INTERVAL);
			};

			wsConnection.onmessage = processWebsocketEvent;

			wsConnection.onclose = (event) => {
				setConnectionStatus(WEBSOCKET_STATUS.DISCONNECTED);

				if (pingIntervalRef.current) {
					clearInterval(pingIntervalRef.current);
					pingIntervalRef.current = null;
				}

				const isAuthError =
					[1006, 4001, 4003].includes(event.code) ||
					event.reason?.includes('INVALID_SIGNATURE') ||
					event.reason?.includes('expired');

				if (isAuthError && isMountedRef.current && !isRefreshingRef.current) {
					refreshConnectionUrl();
				}
			};

			wsConnection.onerror = (errorEvent) => {
				console.error('[Barry] WebSocket error', errorEvent);

				retryCountRef.current += 1;

				if (retryCountRef.current >= MAX_RETRIES && !isRefreshingRef.current) {
					refreshConnectionUrl();
				}
			};

			setConnection(url, wsConnection);
			currentUrlRef.current = url;
		},
		[
			getConnection,
			setConnection,
			teardownConnection,
			processWebsocketEvent,
			refreshConnectionUrl,
		],
	);

	useEffect(() => {
		isMountedRef.current = true;

		if (!connectionData?.url) {
			return;
		}

		// only setup if this is a different URL than current
		if (connectionData.url !== currentUrlRef.current) {
			setupWebSocket(connectionData.url);
		}

		// setup proactive refresh interval (only once)
		if (!urlRefreshIntervalRef.current) {
			urlRefreshIntervalRef.current = setInterval(() => {
				refreshConnectionUrl();
			}, URL_REFRESH_INTERVAL);
		}

		return () => {
			isMountedRef.current = false;
			clearAllIntervals();

			if (currentUrlRef.current) {
				teardownConnection(currentUrlRef.current);
				currentUrlRef.current = null;
			}
		};
	}, [
		connectionData?.url,
		setupWebSocket,
		refreshConnectionUrl,
		clearAllIntervals,
		teardownConnection,
	]);

	useEffect(() => {
		if (connectionData?.url) {
			const status = getConnectionStatus(connectionData.url);
			setConnectionStatus(status);
		}
	}, [connectionData?.url, getConnectionStatus]);

	return { connectionStatus, refreshConnection: refreshConnectionUrl };
};
